import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { NavLink, Outlet, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Input, Tooltip, message } from 'antd';
import {
  AppstoreOutlined,
  BarChartOutlined,
  FileSearchOutlined,
  ThunderboltOutlined,
  PoweroffOutlined,
  SearchOutlined,
  TableOutlined,
} from '@ant-design/icons';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from 'services/firebase';
import { useEncephalon } from 'contexts/EncephalonContext';
import {
  exploreApi,
  useGetOverviewQuery,
  useRandomStudyMutation,
  type StudyDetail,
} from 'services/exploreService';
import './shell.css';

const NAV = [
  { to: '/', label: 'Overview', icon: <BarChartOutlined />, end: true },
  { to: '/studies', label: 'Studies', icon: <TableOutlined /> },
  { to: '/dicoms', label: 'DICOMs', icon: <AppstoreOutlined /> },
  { to: '/search', label: 'Report search', icon: <FileSearchOutlined /> },
];

/** Route a lookup: DICOM ICIDs open the DICOM page, study ICIDs the study, anything else searches reports. */
export function routeForQuery(raw: string): string | null {
  const q = raw.trim();
  if (!q) return null;
  if (/^di-/i.test(q)) return `/dicoms/${encodeURIComponent(q)}`;
  if (/^st-/i.test(q) || /^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(q))
    return `/studies/${encodeURIComponent(q)}`;
  return `/search?q=${encodeURIComponent(q)}`;
}

function localUser() {
  try {
    const token = localStorage.getItem('access_token');
    if (!token) return null;
    const payload = JSON.parse(atob(token.split('.')[1]));
    return {
      displayName: payload.name || '',
      email: payload.email || '',
      photoURL: payload.picture || '',
    };
  } catch {
    return null;
  }
}

const n = (x: number) => x.toLocaleString('en-US');

/**
 * Console layout (same structure as the annotation review panel): a left rail with navigation
 * and live counts per view and per source, and a scrolling main column.
 */
const AppShell: React.FC = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [params] = useSearchParams();
  const [user] = useAuthState(auth);
  const { clearEncephalonState } = useEncephalon();
  const [query, setQuery] = useState('');
  const { data: overview } = useGetOverviewQuery();
  const dispatch = useDispatch<any>();
  const [randomStudy] = useRandomStudyMutation();
  const [randomLoading, setRandomLoading] = useState(false);
  // The next random study is picked ahead of time and its detail fetched into the cache,
  // so the Random button opens it instantly; the first few thumbnails are warmed too.
  const nextRandom = useRef<Promise<string | null> | null>(null);
  const preloadRandom = useCallback(() => {
    nextRandom.current = randomStudy()
      .unwrap()
      .then(async ({ icid }) => {
        const req = dispatch(exploreApi.endpoints.getStudyDetail.initiate(icid, { subscribe: false }));
        try {
          const detail: StudyDetail = await req.unwrap();
          detail.dicoms.slice(0, 8).forEach(d => {
            if (d.media.poster) new Image().src = d.media.poster;
          });
        } catch {
          /* the study page fetches it again if this failed */
        }
        return icid;
      })
      .catch(() => null);
    return nextRandom.current;
  }, [randomStudy, dispatch]);
  useEffect(() => {
    // Wait a moment so the preload doesn't compete with the page's own first queries.
    const t = window.setTimeout(() => {
      if (!nextRandom.current) preloadRandom();
    }, 2500);
    return () => window.clearTimeout(t);
  }, [preloadRandom]);
  const openRandomStudy = async () => {
    setRandomLoading(true);
    const icid = (await nextRandom.current) ?? (await preloadRandom());
    setRandomLoading(false);
    if (!icid) {
      message.error('Could not pick a random study');
      preloadRandom();
      return;
    }
    navigate(`/studies/${encodeURIComponent(icid)}`);
    preloadRandom();
  };
  const who = user || localUser();
  const name = who?.displayName || who?.email?.split('@')[0] || '';

  const logout = async () => {
    await auth.signOut().catch(() => undefined);
    localStorage.removeItem('access_token');
    localStorage.removeItem('firebase_id_token');
    clearEncephalonState();
    window.dispatchEvent(new Event('storage'));
    navigate('/login', { replace: true });
  };

  const t = overview?.totals;
  const techLabelled = overview?.label_coverage.find(c => c.key === 'view_label')?.dicoms ?? 0;
  const activeView = pathname === '/dicoms' ? params.get('view') : null;
  const activeSource = pathname === '/studies' ? params.get('datasource') : null;

  return (
    <div className="ax-shell">
      <aside className="ax-rail" aria-label="Navigation">
        <div className="ax-rail-head">
          <div className="ax-brand-row">
            <NavLink to="/" className="ax-brand">
              <span className="mark" aria-hidden>
                <svg viewBox="0 0 24 24" width="18" height="18">
                  <path
                    d="M2 13h4l2-6 4 12 3-9 2 3h5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              <h1>Atlas</h1>
            </NavLink>
            <Tooltip title="Open a random study" placement="right">
              <button
                type="button"
                className="ax-random"
                onClick={openRandomStudy}
                disabled={randomLoading}
                aria-label="Open a random study"
              >
                <ThunderboltOutlined spin={randomLoading} />
                <span>Random</span>
              </button>
            </Tooltip>
          </div>
          <div className="sub">
            {t
              ? `${n(t.studies)} studies · ${n(t.dicoms)} DICOMs · ${n(techLabelled)} tech-labelled`
              : 'loading…'}
          </div>
          <Input
            size="small"
            prefix={<SearchOutlined />}
            placeholder="ICID, or search reports…"
            aria-label="Go to study or DICOM"
            value={query}
            allowClear
            onChange={e => setQuery(e.target.value)}
            onPressEnter={() => {
              const to = routeForQuery(query);
              if (to) {
                navigate(to);
                setQuery('');
              }
            }}
          />
        </div>

        <div className="ax-rail-scroll">
          <nav aria-label="Main">
            {NAV.map(item => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) => `ax-rail-item nav ${isActive ? 'sel' : ''}`}
              >
                {item.icon}
                <span className="nm">{item.label}</span>
              </NavLink>
            ))}
          </nav>

          {overview && overview.by_view.length > 0 && (
            <>
              <div className="kindh">Views · AI predicted / tech labelled</div>
              {overview.by_view.map(v => {
                const sel = activeView === v.view;
                const share = v.predicted
                  ? Math.min(100, (100 * v.labeled) / v.predicted)
                  : v.labeled
                    ? 100
                    : 0;
                return (
                  <button
                    type="button"
                    key={v.view}
                    className={`ax-rail-item ${sel ? 'sel' : ''}`}
                    aria-pressed={sel}
                    onClick={() =>
                      navigate(sel ? '/dicoms' : `/dicoms?view=${encodeURIComponent(v.view)}`)
                    }
                    title={`${v.view}: ${n(v.predicted)} predicted by AI, ${n(v.labeled)} labelled by a tech`}
                  >
                    <span className="nm">{v.view}</span>
                    <span className="ct">
                      {n(v.predicted)} / {n(v.labeled)}
                    </span>
                    <span className="bar" aria-hidden>
                      <i style={{ width: `${share}%`, background: 'var(--s2)' }} />
                    </span>
                  </button>
                );
              })}
            </>
          )}

          {overview && overview.by_datasource.length > 0 && (
            <>
              <div className="kindh">Sources · studies</div>
              {overview.by_datasource.map(s => {
                const sel = !!s.id && activeSource === s.id;
                const top = overview.by_datasource[0]?.studies || 1;
                return (
                  <button
                    type="button"
                    key={s.id ?? s.name}
                    className={`ax-rail-item ${sel ? 'sel' : ''}`}
                    aria-pressed={sel}
                    disabled={!s.id}
                    onClick={() =>
                      navigate(
                        sel ? '/studies' : `/studies?datasource=${encodeURIComponent(s.id!)}`
                      )
                    }
                  >
                    <span className="nm">{s.name}</span>
                    <span className="ct">{n(s.studies)}</span>
                    <span className="bar" aria-hidden>
                      <i
                        style={{ width: `${(100 * s.studies) / top}%`, background: 'var(--s1)' }}
                      />
                    </span>
                  </button>
                );
              })}
            </>
          )}
        </div>

        {who && (
          <div className="ax-rail-foot">
            {who.photoURL ? (
              <img src={who.photoURL} alt="" referrerPolicy="no-referrer" />
            ) : (
              <span className="avatar">{(name[0] || 'U').toUpperCase()}</span>
            )}
            <span className="uname" title={who.email}>
              {name || who.email}
            </span>
            <button
              type="button"
              className="ax-icon-btn"
              onClick={logout}
              aria-label="Sign out"
              title="Sign out"
            >
              <PoweroffOutlined />
            </button>
          </div>
        )}
      </aside>
      <main className="ax-main">
        <Outlet />
      </main>
    </div>
  );
};

export default AppShell;
